/**
 * The product's name, its address, and the sentences that carry both off-site.
 *
 * ## Why this is a module and not three string literals
 *
 * By milestone 7 the product's name and the site's URL appear in the browser tab, the boot splash,
 * the main menu, the credits screen, the screenshot watermark, the share sheet, the copied post
 * line, the downloaded file name, the installable manifest, four Open Graph tags and two Twitter
 * tags. That is a dozen places, in three languages (TypeScript, HTML, and JSON), and the failure
 * mode of a dozen literals is not that one of them is wrong — it is that one of them is wrong *for
 * a while*, on the social card, which is the copy nobody looks at because it only renders inside
 * somebody else's website.
 *
 * So there is one definition, and the three files that cannot import it — `index.html`,
 * `public/manifest.webmanifest` and `README.md` — are checked against it by
 * {@link file://./brand.test.ts}. A mismatch is a failing test rather than a wrong link. That test
 * also refuses to let any user-facing module spell the name out, which is what made renaming the
 * product from its working title a single edit here.
 *
 * Internal identifiers are deliberately *not* derived from this: the repo folder, the
 * `localStorage` key prefix, the service-worker cache names, the DOM element ids and a handful of
 * three.js debug labels all still read `skysim`. Renaming the storage prefix would silently
 * discard every pilot's saved settings and bindings, and renaming the rest would be churn.
 *
 * ## The address is real as of v1.0.0, and the placeholder earned its keep on the way out
 *
 * Until the deploy this held `https://openskies.example`, deliberately unreachable, because a
 * plausible *guess* produces a watermark and an `og:url` that are silently wrong — a link that
 * 404s reads as a broken product, whereas an obvious placeholder reads as a build that has not
 * shipped, which was the truth.
 *
 * It paid off at the last possible moment. The URL handed over at launch was
 * `openskies.pages.dev`, which turns out to belong to **somebody else's website** — a stock-photo
 * site — because `openskies` was already taken on Cloudflare Pages and the project had landed on
 * a fallback name. Because nothing here had been filled in speculatively, the wrong address was
 * caught by fetching it and comparing against the build rather than by a user eventually noticing
 * that the watermark on their screenshot pointed at photographs of clouds.
 *
 * The lesson for whoever changes {@link SITE_URL} next: **fetch it and check it serves this
 * build.** A URL that resolves is not evidence that it resolves to you.
 *
 * `updateFixed` / `updateFrame`: neither. Constants and two pure string builders.
 */

/** The product name, capitalised exactly as it should appear anywhere a human reads it. */
export const PRODUCT_NAME = 'OpenSkies';

/**
 * What the product is, in one line, for a page title and a social card.
 *
 * Kept under about seventy characters: Open Graph descriptions are truncated by most consumers
 * somewhere around there, and a sentence cut mid-clause looks worse than a short one.
 */
export const TAGLINE = 'An ultra-realistic flight simulator that runs in the browser';

/**
 * The longer blurb: `og:description`, the meta description, and the store-style copy.
 *
 * The aircraft count is spelled out here and **pinned against the registry** by `brand.test.ts`.
 * It said "three" for two milestones after the fourth and fifth aeroplanes shipped, and the test
 * that exists to stop exactly this did not catch it: it asserts that `index.html`, the manifest
 * and this constant all say the *same* thing, which they did — all three were wrong together.
 * Consistency is not truth, and a copy constant that states a fact about the code needs a test
 * against the code, not against its own copies.
 */
export const DESCRIPTION =
  'Real Earth terrain and real airports streamed from open data, a study-level flight model, ' +
  'five aircraft, and fully procedural sound — all in a browser tab, no install.';

/**
 * Where the simulator lives.
 *
 * `flyopenskies` rather than `openskies` because the plain name was already taken on Cloudflare
 * Pages by an unrelated site — the project name *is* the subdomain and it is global across all of
 * Pages. A custom domain can be attached later without touching the build; a `pages.dev` address
 * keeps working after one is, so nothing shared today goes stale.
 *
 * No trailing slash: everything that builds on it appends a path, and `${BASE}/x` against a
 * trailing slash gives `//x`, which is a protocol-relative URL and resolves somewhere else
 * entirely. Getting that wrong in an `og:image` is invisible until a link preview is blank.
 */
export const SITE_URL = 'https://flyopenskies.pages.dev';

/** The address as it should be *written* — no scheme, because a watermark is not a link. */
export const SITE_DOMAIN = 'flyopenskies.pages.dev';

/** Where the social card lives, relative to the site root. Absolute-ised for `og:image`. */
export const SOCIAL_CARD_PATH = '/social-card.png';

/** Open Graph wants an absolute URL; relative ones are ignored by most scrapers. */
export const SOCIAL_CARD_URL = `${SITE_URL}${SOCIAL_CARD_PATH}`;

/** What the browser tab says. Name first, so it survives a narrow tab. */
export const PAGE_TITLE = `${PRODUCT_NAME} — browser flight simulator`;

/**
 * The line that goes out with a shared screenshot.
 *
 * Written once, here, rather than composed at the call site, because it is *copy* — it gets
 * rewritten by whoever is better at writing than the person who wired the button, and they should
 * be able to find it. Deliberately free of hashtags and @-mentions: those belong to whichever
 * network the pilot is posting to, and guessing wrong makes the post look automated.
 *
 * @param where Optional place name — an ICAO, or "Innsbruck". Omitted when nothing is known,
 *   which is the case before the airport database lands.
 */
export function shareText(where?: string | null, flight?: SharedFlight | null): string {
  const place = where === null || where === undefined || where.trim() === '' ? null : where.trim();
  const opening = place === null ? `Flying in ${PRODUCT_NAME}` : `Flying out of ${place} in ${PRODUCT_NAME}`;
  return `${opening} — a study-level flight simulator that runs in a browser tab. ${shareUrl(flight)}`;
}

/**
 * The flight a shared link should open into. Every field optional; all of them are copied.
 *
 * This is an **allow-list**, and that is the whole design. The obvious implementation is to append
 * `location.search` to the site URL, and it is wrong: the sharer's address bar may carry
 * `?tier=low`, `?forcewebgl=1`, `?perf=…` or a screenshot-harness flag, and a shared link would
 * pin a stranger to whatever the sharer happened to be debugging. Only these five keys travel, and
 * a new one has to be added here deliberately.
 */
export interface SharedFlight {
  /** Departure airport, ICAO. */
  icao?: string | null;
  /** Aircraft id from the registry. */
  aircraft?: string | null;
  /** Time-of-day preset name. */
  time?: string | null;
  /** Weather preset name. */
  weather?: string | null;
}

/**
 * The site URL with the flight in it, so a shared screenshot opens the aeroplane it shows.
 *
 * Without this, `shareText` posted a bare `SITE_URL`: a screenshot of the fighter on short final
 * into Innsbruck linked to the trainer, parked at the default field, behind the main menu. The
 * picture and the link described different flights.
 *
 * `nomenu=1` rides along whenever there is anything to open into — a link that lands on a menu
 * makes the recipient do the work of reconstructing what they were just shown.
 */
export function shareUrl(flight?: SharedFlight | null): string {
  if (flight === null || flight === undefined) return SITE_URL;
  const params = new URLSearchParams();
  const put = (key: string, value: string | null | undefined): void => {
    if (typeof value === 'string' && value.trim() !== '') params.set(key, value.trim());
  };
  put('icao', flight.icao);
  put('aircraft', flight.aircraft);
  put('time', flight.time);
  put('weather', flight.weather);
  if ([...params.keys()].length === 0) return SITE_URL;
  params.set('nomenu', '1');
  return `${SITE_URL}?${params.toString()}`;
}

/** The file name a downloaded screenshot lands under. Sortable, and safe on every filesystem. */
export function screenshotFileName(stamp: Date, where?: string | null): string {
  const pad = (value: number): string => String(value).padStart(2, '0');
  const date =
    `${stamp.getFullYear()}${pad(stamp.getMonth() + 1)}${pad(stamp.getDate())}` +
    `-${pad(stamp.getHours())}${pad(stamp.getMinutes())}${pad(stamp.getSeconds())}`;
  // Anything that is not a letter, a digit or a dash is dropped rather than replaced: an ICAO is
  // already safe, and a runway identifier like "28L/10R" would otherwise become "28L-10R" and
  // read as two runways.
  const place = (where ?? '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
  // Derived from the name rather than written out: a downloads folder is a user-visible surface,
  // and a file prefix is exactly the sort of string a rename forgets.
  const prefix = PRODUCT_NAME.toLowerCase();
  return place === ''
    ? `${prefix}-${date}.png`
    : `${prefix}-${place}-${date}.png`;
}
